import { useEffect, useState } from "react";
import { getStripeReconciliation } from "services/finance.service";

const formatCurrency = (value) =>
  `$${Number(value ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const statusClass = (ok) =>
  ok
    ? "border-green-200 bg-green-50 text-green-800"
    : "border-amber-200 bg-amber-50 text-amber-800";

export default function StripeReconciliationPanel({ api }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getStripeReconciliation(api);
        if (!cancelled) setData(result ?? null);
      } catch {
        if (!cancelled) {
          setData(null);
          setError("Failed to load Stripe reconciliation.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const isConsistent =
    data &&
    Math.abs(data.difference) <= 0.01 &&
    data.missingTransactions?.length === 0 &&
    data.duplicateTransactions?.length === 0 &&
    data.refundMismatch?.length === 0;

  return (
    <div className="mb-6">
      <h2 className="text-lg font-medium text-gray-900 mb-3">
        Stripe Income Consistency
      </h2>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {isLoading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 animate-pulse h-32" />
      )}

      {!isLoading && data && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-4">
          <div
            className={`rounded-lg border px-4 py-3 text-sm font-medium ${statusClass(isConsistent)}`}
          >
            {isConsistent
              ? "Stripe and database revenue are in sync."
              : "Reconciliation issues detected - review details below."}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Stripe Revenue</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatCurrency(data.totalStripeRevenue)}
              </p>
            </div>
            <div>
              <p className="text-gray-500">DB Revenue</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatCurrency(data.totalDBRevenue)}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Difference</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatCurrency(data.difference)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="rounded border border-gray-100 bg-gray-50 px-3 py-2">
              <p className="text-gray-500">Missing</p>
              <p className="font-semibold">{data.missingTransactions?.length ?? 0}</p>
            </div>
            <div className="rounded border border-gray-100 bg-gray-50 px-3 py-2">
              <p className="text-gray-500">Duplicates</p>
              <p className="font-semibold">{data.duplicateTransactions?.length ?? 0}</p>
            </div>
            <div className="rounded border border-gray-100 bg-gray-50 px-3 py-2">
              <p className="text-gray-500">Refund Mismatches</p>
              <p className="font-semibold">{data.refundMismatch?.length ?? 0}</p>
            </div>
            <div className="rounded border border-gray-100 bg-gray-50 px-3 py-2">
              <p className="text-gray-500">Sub Mismatches</p>
              <p className="font-semibold">{data.subscriptionMismatches?.length ?? 0}</p>
            </div>
          </div>

          {data.innerCircle && (
            <div className="border-t border-gray-100 pt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Inner Circle Sync
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">Active</p>
                  <p className="font-semibold">{data.innerCircle.activeSubscribers}</p>
                </div>
                <div>
                  <p className="text-gray-500">MRR</p>
                  <p className="font-semibold">{formatCurrency(data.innerCircle.mrr)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Expected MRR</p>
                  <p className="font-semibold">
                    {formatCurrency(data.innerCircle.expectedMrr)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Churn %</p>
                  <p className="font-semibold">{data.innerCircle.churnPercent}%</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
